import { useCallback } from "react";

import {
  PHASE2_GENERATE_OTP,
  PHASE2_VALIDATE,
} from "../../../../../config/Endpoints.js";
import { getAuthToken } from "../../../../../utils/Storage.js";
import { getApi, postEmptyBody, postUrlEncoded, ZOD_PARSE_ERROR } from "../../../../utils/api.ts";
import { useSqeOnboardingApi } from "../../onboarding/sqe/sqe-onboarding-link-context.tsx";
import { SQE_PHASE2, unwrapSqeData } from "../../onboarding/sqe/sqe-onboarding-api.ts";
import {
  phase2ValidateSuccessSchema,
  type Phase2ValidateSuccess,
} from "../../onboard-post-join/schemas/onboard-post-join.schema.ts";
import {
  generateOtpResponseSchema,
  type GenerateOtpResponse,
} from "../schemas/onboard-verify.schema.ts";

function accessToken(): string | undefined {
  const token = getAuthToken("accessToken");
  return token != null && token !== "" ? token : undefined;
}

export function usePostjoinOtp() {
  const isSqe = useSqeOnboardingApi();
  const generateOtp = useCallback(
    async (
      token: string,
      email: string,
      signal?: AbortSignal,
    ): Promise<GenerateOtpResponse> => {
      const params = new URLSearchParams();
      params.set("token", token);
      params.set("email", email);
      const { data } = isSqe
        ? await postEmptyBody<unknown>(`${SQE_PHASE2}/generate-otp?${params.toString()}`, undefined, signal)
        : await postUrlEncoded<unknown>(PHASE2_GENERATE_OTP, accessToken(), params, signal);
      const parsed = generateOtpResponseSchema.safeParse(isSqe ? unwrapSqeData(data) : data);
      if (!parsed.success) {
        throw new Error(ZOD_PARSE_ERROR);
      }
      return parsed.data;
    },
    [isSqe],
  );

  const validateOtp = useCallback(
    async (
      token: string,
      email: string,
      otp: string,
      signal?: AbortSignal,
    ): Promise<Phase2ValidateSuccess> => {
      const params = new URLSearchParams();
      params.set("token", token);
      params.set("email", email);
      params.set("otp", otp);
      const { data } = isSqe
        ? await postEmptyBody<unknown>(`${SQE_PHASE2}/validate?${params.toString()}`, undefined, signal)
        : await postUrlEncoded<unknown>(PHASE2_VALIDATE, accessToken(), params, signal);
      const sqeBody = isSqe ? unwrapSqeData(data) : data;
      const parsed = phase2ValidateSuccessSchema.safeParse(isSqe && sqeBody && typeof sqeBody === "object"
        ? { employeeName: "", ...sqeBody as Record<string, unknown> }
        : sqeBody);
      if (!parsed.success) {
        throw new Error(ZOD_PARSE_ERROR);
      }
      const body = parsed.data;
      if (!Number.isFinite(body.employeeId)) {
        throw new Error(body.message || "Missing employee reference.");
      }
      if (isSqe && !body.employeeName) {
        try {
          const { data: detailsResponse } = await getApi<unknown>(`${SQE_PHASE2}/details/${encodeURIComponent(String(body.employeeId))}`, undefined, "json", signal);
          const details = unwrapSqeData(detailsResponse);
          if (details && typeof details === "object") {
            const record = details as Record<string, unknown>;
            const employee = record.employee && typeof record.employee === "object" ? record.employee as Record<string, unknown> : record;
            const employeeName = [employee.firstName, employee.lastName].filter((part) => typeof part === "string" && part.trim()).join(" ");
            if (employeeName) return { ...body, employeeName };
          }
        } catch {
          // The validated employee ID is sufficient for the existing form.
        }
      }
      return parsed.data;
    },
    [isSqe],
  );

  return { generateOtp, validateOtp };
}
